import { trpc } from './trpc'
import { inferRouterOutputs, inferRouterInputs } from '@trpc/server'
import { AppRouter } from '@tagadapay/trpc-types'

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>

// `getLatestByStoreAndType` is `.nullable()` server-side → strip null for the row shape.
export type StoreTask = NonNullable<OutputRouter['tasks']['getLatestByStoreAndType']>
export type StoreTaskType = InputRouter['tasks']['getLatestByStoreAndType']['type']

/** Poll cadence while a background task is still pending/processing. */
const ACTIVE_TASK_POLL_INTERVAL_MS = 2500

const isActiveTaskStatus = (status?: string | null) =>
  status === 'pending' || status === 'processing'

/**
 * Latest background task of a given type for a store (e.g. a Shopify/WooCommerce
 * product sync). Polls automatically while the task is pending/processing and
 * stops once it reaches a terminal state, so callers can react to completion
 * without owning the polling loop. Pass `type: null` to disable the query (e.g.
 * manual stores that have no background sync).
 */
export const useLatestStoreTask = (
  storeId: string,
  type: StoreTaskType | null,
  options?: { enabled?: boolean },
) => {
  return trpc.tasks.getLatestByStoreAndType.useQuery(
    { storeId, type: type as StoreTaskType },
    {
      enabled: (options?.enabled ?? true) && Boolean(storeId) && Boolean(type),
      refetchInterval: (query: { state: { data?: StoreTask | null } }) =>
        isActiveTaskStatus(query.state.data?.status) ? ACTIVE_TASK_POLL_INTERVAL_MS : false,
    },
  )
}
